import { GetUsersSuggestionsResponseDTO } from "@/app/api/users/suggestions/users-suggestions.dto";
import { USERS_SUGGESTIONS_API } from "@/lib/constants";
import { MentionOptions } from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import MentionList, { MentionListRef } from "./mention-list";
import { PluginKey } from "@tiptap/pm/state";

export type MentionSuggestion = {
	id: string;
	label: string;
	image?: string | null;
};

const DOM_RECT_FALLBACK: DOMRect = {
	bottom: 0,
	height: 0,
	left: 0,
	right: 0,
	top: 0,
	width: 0,
	x: 0,
	y: 0,
	toJSON() {
		return {};
	},
};

export const mentionSuggestionOptions: MentionOptions["suggestion"] = {
	char: "@",
	pluginKey: new PluginKey("profile-mention"),
	items: async ({ query }) => {
		try {
			const url = new URL(USERS_SUGGESTIONS_API);
			url.searchParams.set("query", query);

			const response = await fetch(url);
			const { data } = (await response.json()) as { data: GetUsersSuggestionsResponseDTO };

			return data
				.filter((item) => item.name?.toLowerCase().includes(query.toLowerCase()))
				.slice(0, 6)
				.map((item) => ({ id: item.username, label: item.name, image: item.image }));
		} catch (error) {
			console.error("Error fetching data:", error);
			return [];
		}
	},

	render: () => {
		let component: ReactRenderer<MentionListRef> | undefined;
		let popup: TippyInstance | undefined;

		return {
			onStart: (props) => {
				component = new ReactRenderer(MentionList, {
					props,
					editor: props.editor,
				});

				popup = tippy("body", {
					getReferenceClientRect: () => props.clientRect?.() ?? DOM_RECT_FALLBACK,
					appendTo: () => document.body,
					content: component.element,
					showOnCreate: true,
					interactive: true,
					trigger: "manual",
					placement: "bottom-start",
				})[0];
			},

			onUpdate(props) {
				component?.updateProps(props);

				popup?.setProps({
					getReferenceClientRect: () => props.clientRect?.() ?? DOM_RECT_FALLBACK,
				});
			},

			onKeyDown(props) {
				if (props.event.key === "Escape") {
					popup?.hide();

					return true;
				}

				if (!component?.ref) {
					return false;
				}

				return component?.ref?.onKeyDown(props);
			},

			onExit() {
				popup?.destroy();
				component?.destroy();

				popup = undefined;
				component = undefined;
			},
		};
	},
};
